import React from "react";
import { Truck, Plane, Ship, Package, Globe2, ArrowRight, MapPin } from "lucide-react";
import { Link } from "react-router-dom";
import Reveal from "./Reveal";

const countries = [
  "Italija", "Njemačka", "Holandija", "Belgija", "Hrvatska",
  "Poljska", "Češka", "Mađarska", "Slovačka", "Srbija", "Turska", "Francuska",
];

const services = [
  { icon: Truck, title: "Cestovni transport", desc: "LTL i FTL utovari kroz cijelu Evropu." },
  { icon: Plane, title: "Avio transport", desc: "Brza dostava za hitne pošiljke." },
  { icon: Ship, title: "Pomorski transport", desc: "Ekonomično rješenje za veće količine." },
  { icon: Package, title: "Intermodalni", desc: "Kombinovani transport na svim relacijama." },
];

export default function LogisticsSection() {
  return (
    <section className="relative py-24 md:py-32 bg-white overflow-hidden">
      <div className="max-w-7xl mx-auto px-4 md:px-8 grid lg:grid-cols-12 gap-12 items-center">
        {/* Left: text */}
        <div className="lg:col-span-6">
          <Reveal>
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-neutral-100 text-xs uppercase tracking-widest text-neutral-700">
              <Globe2 className="w-3.5 h-3.5 text-amber-500" />
              Logistika & Transport
            </div>
          </Reveal>
          <Reveal delay={100}>
            <h2 className="font-display mt-5 text-4xl md:text-5xl lg:text-6xl font-bold leading-tight text-neutral-950">
              Sveobuhvatne <span className="text-amber-500">logističke</span> usluge.
            </h2>
          </Reveal>
          <Reveal delay={200}>
            <div className="mt-6 space-y-4 text-neutral-700 leading-relaxed text-base md:text-lg">
              <p>
                Nudimo sveobuhvatne logističke usluge: <strong>uvoz i izvoz robe</strong>, kao i transport <strong>od vrata do vrata</strong> – cestovni, avio, pomorski i intermodalni. Kao međunarodna i porodična logistička kompanija, nudimo fleksibilna rješenja prilagođena vašim potrebama i industriji. Bez obzira na destinaciju – međunarodno ili domaće tržište – i vrstu robe, pronaći ćemo optimalan način transporta za vas.
              </p>
              <p>
                Zahvaljujući našoj širokoj i dobro povezanoj mreži cestovnog transporta, nudimo prilagođena logistička rješenja za transport vaše robe – gotovo iz bilo koje destinacije i obrnuto. Za veće pošiljke obezbjeđujemo konkurentne usluge djelimičnih utovara (<strong>LTL</strong> – manje od punog kamiona) i punih utovara (<strong>FTL</strong> – puni kamioni).
              </p>
            </div>
          </Reveal>

          <Reveal delay={300}>
            <div className="mt-8">
              <div className="text-xs uppercase tracking-widest text-neutral-500 mb-3">Specijalizirani smo za</div>
              <div className="flex flex-wrap gap-2">
                {countries.map((c) => (
                  <span
                    key={c}
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-neutral-100 text-neutral-800 text-sm border border-neutral-200 hover:border-amber-400 hover:bg-amber-50 transition-colors"
                  >
                    <MapPin className="w-3 h-3 text-amber-500" />
                    {c}
                  </span>
                ))}
                <span className="inline-flex items-center px-3 py-1.5 rounded-full bg-neutral-950 text-white text-sm">
                  i druge zemlje
                </span>
              </div>
            </div>
          </Reveal>

          <Reveal delay={400}>
            <Link
              to="/kontakt"
              className="btn-primary mt-8 inline-flex items-center gap-2 bg-neutral-950 hover:bg-amber-400 hover:text-neutral-950 text-white px-6 py-3.5 rounded-full font-medium"
            >
              Zatraži ponudu za transport <ArrowRight className="w-4 h-4" />
            </Link>
          </Reveal>
        </div>

        {/* Right: image + service cards */}
        <div className="lg:col-span-6 relative">
          <Reveal delay={200}>
            <div className="relative rounded-3xl overflow-hidden border border-neutral-200 shadow-2xl">
              <img
                src="https://images.unsplash.com/photo-1583686298564-46fbffda0707?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200"
                alt="Međunarodni transport i logistika"
                className="w-full h-[420px] object-cover"
                loading="lazy"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-neutral-950/70 via-neutral-950/10 to-transparent" />
              <div className="absolute bottom-5 left-5 right-5 flex items-center gap-3 bg-white/95 backdrop-blur rounded-2xl p-4">
                <div className="w-11 h-11 rounded-xl bg-amber-400 flex items-center justify-center shrink-0">
                  <Globe2 className="w-6 h-6 text-neutral-950" />
                </div>
                <div>
                  <div className="font-semibold text-sm text-neutral-950">Međunarodna porodična logistička kompanija</div>
                  <div className="text-xs text-neutral-600">Fleksibilna rješenja za svaku industriju</div>
                </div>
              </div>
            </div>
          </Reveal>

          <Reveal delay={300}>
            <div className="mt-4 grid grid-cols-2 gap-3">
              {services.map((s) => (
                <div
                  key={s.title}
                  className="card-hover bg-neutral-50 border border-neutral-200 rounded-2xl p-4 hover:border-amber-400 hover:bg-white hover:shadow-lg"
                >
                  <div className="w-10 h-10 rounded-xl bg-neutral-950 flex items-center justify-center">
                    <s.icon className="w-5 h-5 text-amber-400" />
                  </div>
                  <div className="mt-3 font-semibold text-sm text-neutral-950">{s.title}</div>
                  <div className="text-xs text-neutral-600 mt-1">{s.desc}</div>
                </div>
              ))}
            </div>
          </Reveal>
        </div>
      </div>
    </section>
  );
}
